/**
 * Client-side validation of a schema form's value against its compiled tree
 * (`compileSchemaToFormTree`) — hint-only; the chokepoint's Ajv stays the
 * authority and sees the same value afterwards.
 *
 * Lives in the backend tree beside `pattern.ts` so it is unit-tested, and is
 * aliased into the SPA as `@layer/dashboard/schema-form/validate`. It measures
 * the way the chokepoint does: a `pattern` through `compileSchemaPattern` (the
 * `u` flag) and lengths in code points (schema-form-unicode-regex, 2026-10-29).
 *
 * It walks the whole tree (schema-form-native-attrs, 2026-10-30): nested
 * objects, array items and a nested `oneOf`'s variant — the one whose
 * discriminator field the value carries — so a bound on a nested leaf is
 * answered before Dispatch rather than only by the server. Until then only the
 * root's own leaves were checked, and the native `maxLength` attribute, since
 * removed, was the only cap a nested text input had. Error keys are value paths
 * (`claims.1.claim`); `errorForField` folds them under the root field for the
 * row that shows them.
 *
 * Its words are the server's (field-errors, 2026-11-01, Mario's box 70 Q7): a
 * format, a length or number limit and a choice list are told in the words of
 * `@layer/canonical-actions/field-fix-words` — the table a refusal's `fields`
 * come from — so the form's check and the server's answer say the same thing.
 * A pattern with no words there (an id, an internal name) keeps naming the
 * pattern, as before.
 */
import {
  choiceFix,
  FIX_WORDS_BY_PATTERN,
  limitFix,
} from "@layer/canonical-actions/field-fix-words";
import type {
  FormObjectNode,
  FormOneOfNode,
  FormOneOfVariant,
  FormTreeNode,
} from "./compiler.js";
import { compileSchemaPattern, schemaStringLength } from "./pattern.js";

/** Error messages keyed by value path: a root field name, or `field.index.sub` below it. */
export type FormErrors = Record<string, string>;

const isBlank = (v: unknown): boolean => v === undefined || v === null || v === "";

const isPlainObject = (v: unknown): v is Record<string, unknown> =>
  typeof v === "object" && v !== null && !Array.isArray(v);

/**
 * Every error in `value` against `tree`, keyed by path. `prefix` is the path of
 * `tree` itself (`""` at the root, `claims.1.` for an array item's object).
 */
export function validateAgainstTree(
  tree: FormObjectNode,
  value: Record<string, unknown>,
  prefix = "",
): FormErrors {
  const errors: FormErrors = {};
  for (const reqField of tree.required) {
    if (isBlank(value[reqField])) errors[`${prefix}${reqField}`] = "Required";
  }
  for (const [fieldName, node] of Object.entries(tree.properties)) {
    const v = value[fieldName];
    if (isBlank(v)) continue;
    validateNode(node, v, `${prefix}${fieldName}`, errors);
  }
  return errors;
}

function validateNode(
  node: FormTreeNode,
  v: unknown,
  path: string,
  errors: FormErrors,
): void {
  switch (node.kind) {
    case "object": {
      if (!isPlainObject(v)) {
        errors[path] = "Expected an object";
        return;
      }
      Object.assign(errors, validateAgainstTree(node, v, `${path}.`));
      return;
    }
    case "array": {
      if (!Array.isArray(v)) {
        errors[path] = "Expected a list";
        return;
      }
      const items: ReadonlyArray<unknown> = v;
      items.forEach((item, index) => {
        if (!isBlank(item)) validateNode(node.itemNode, item, `${path}.${index}`, errors);
      });
      return;
    }
    case "oneOf": {
      if (!isPlainObject(v)) {
        errors[path] = "Expected an object";
        return;
      }
      const variant = activeVariant(node, v);
      if (variant !== undefined) {
        Object.assign(errors, validateAgainstTree(variant.node, v, `${path}.`));
      }
      return;
    }
    case "boolean":
      return;
    default: {
      const err = validateLeaf(node, v);
      if (err !== null) errors[path] = err;
    }
  }
}

/**
 * The variant a nested `oneOf` value is being filled against: the first whose
 * discriminator field the value carries (the renderer resets the value to the
 * chosen variant's defaults, so that field is present). `undefined` when none
 * is — then the server decides.
 */
export function activeVariant(
  node: FormOneOfNode,
  value: Record<string, unknown>,
): FormOneOfVariant | undefined {
  return node.variants.find((variant) =>
    Object.prototype.hasOwnProperty.call(value, variant.discriminator),
  );
}

/** The server's words for a row: "use at most 5 characters" → "Use at most 5 characters". */
function rowWords(fix: string): string {
  return fix.charAt(0).toUpperCase() + fix.slice(1);
}

/** The words for a limit the schema states, as the server says them. */
function limitWords(keyword: string, limit: number): string {
  const fix = limitFix(keyword, limit);
  return fix === null ? `${keyword} ${limit}` : rowWords(fix);
}

/** One leaf's verdict; `null` when it passes and for the container kinds. */
export function validateLeaf(node: FormTreeNode, v: unknown): string | null {
  if (node.kind === "text") {
    if (typeof v !== "string") return "Expected a string";
    // Length as the chokepoint measures it: code points, an emoji counting once.
    const length = schemaStringLength(v);
    if (node.minLength !== undefined && length < node.minLength) {
      return limitWords("minLength", node.minLength);
    }
    if (node.maxLength !== undefined && length > node.maxLength) {
      return limitWords("maxLength", node.maxLength);
    }
    if (node.pattern !== undefined) {
      // The chokepoint's dialect (the `u` flag — an emoji passes a surrogate-range
      // class, a lone surrogate does not); null when the pattern does not compile
      // here, so the form stays quiet and the server's answer stands.
      const re = compileSchemaPattern(node.pattern);
      if (re !== null && !re.test(v)) {
        const words = FIX_WORDS_BY_PATTERN.get(node.pattern);
        return words !== undefined && words !== null
          ? rowWords(words.fix)
          : `Doesn't match pattern ${node.pattern}`;
      }
    }
    return null;
  }
  if (node.kind === "integer") {
    if (typeof v !== "number" || !Number.isFinite(v)) return "Expected a number";
    if (node.minimum !== undefined && v < node.minimum) {
      return limitWords("minimum", node.minimum);
    }
    if (node.maximum !== undefined && v > node.maximum) {
      return limitWords("maximum", node.maximum);
    }
    return null;
  }
  if (node.kind === "enum") {
    if (typeof v !== "string" || !node.values.includes(v)) {
      return rowWords(choiceFix(node.values));
    }
    return null;
  }
  return null;
}

/** `claims.1.claim` → `claims[1].claim`, the way the form labels an array item. */
export function pathLabel(path: string): string {
  return path.replace(/\.(\d+)(?=\.|$)/gu, "[$1]");
}

/**
 * What a root field's row shows: the field's own error, else the first error
 * below it with its path (`claims[1].claim: Maximum length 240`).
 */
export function errorForField(errors: FormErrors, fieldName: string): string | undefined {
  const own = errors[fieldName];
  if (own !== undefined) return own;
  const below = `${fieldName}.`;
  const nested = Object.keys(errors).find((key) => key.startsWith(below));
  if (nested === undefined) return undefined;
  return `${pathLabel(nested)}: ${errors[nested] ?? ""}`;
}

/** `errors` without a root field's own and nested entries — the field was edited. */
export function withoutFieldErrors(errors: FormErrors, fieldName: string): FormErrors {
  const below = `${fieldName}.`;
  return Object.fromEntries(
    Object.entries(errors).filter(([key]) => key !== fieldName && !key.startsWith(below)),
  );
}
